import {
    Home,
    MessageCircle,
    Folder,
    Crown,
    LayoutDashboard,
    Palette,
    List,
    Calendar,
    Handshake,
    UserCheck,
    FileText,
    MessageSquare,
    Tag,
    Globe,
    RefreshCw,
    Database,
    Shield,
    Activity,
    Users,
    Settings,
    LayoutGrid,
    Briefcase,
    Zap,
    Server,
    Receipt,
    Clock,
    UserPlus,
    BookOpen,
    ShoppingCart,
    Repeat,
    Ticket
} from 'lucide-react';
import type { DashboardMode } from '@/context/DashboardContext';

// ──────────────────────────────────────────────────────────────────────────────
// Agency Dashboard Navigation (Internal / Project Management)
// ──────────────────────────────────────────────────────────────────────────────

export const agencyNavigationItems = [
    {
        id: 'dashboard',
        label: 'Home',
        icon: Home,
        path: '/home'
    },
    {
        id: 'inbox',
        label: 'Inbox',
        icon: MessageCircle,
        path: '/inbox'
    },
    {
        id: 'file-manager',
        label: 'Files',
        icon: Folder,
        path: '/files'
    },
    {
        id: 'brand',
        label: 'Brand',
        icon: Crown,
        path: '/brand',
        hasSubItems: true,
        subItems: [
            {
                id: 'brand-dashboard',
                label: 'Brand Kits',
                icon: LayoutDashboard,
                path: '/brand'
            },
            {
                id: 'blueprints',
                label: 'Blueprints',
                icon: Palette,
                path: '/brand/blueprints'
            }
        ]
    },
    {
        id: 'boards',
        label: 'Boards',
        icon: LayoutDashboard,
        path: '/task-board',
        hasSubItems: true,
        subItems: [
            {
                id: 'projects',
                label: 'Projects',
                icon: Briefcase,
                path: '/boards/projects'
            },
            {
                id: 'task-board',
                label: 'Tasks',
                icon: List,
                path: '/task-board'
            },
            {
                id: 'time-tracker',
                label: 'Timesheet',
                icon: Clock,
                path: '/boards/time-tracker'
            },
            {
                id: 'knowledge-base',
                label: 'Knowledge Base',
                icon: BookOpen,
                path: '/boards/knowledge-base'
            }
        ]
    },
    {
        id: 'sites',
        label: 'Sites',
        icon: Globe,
        path: '/sites',
        hasSubItems: true,
        subItems: [
            {
                id: 'my-websites',
                label: 'My Websites',
                icon: Globe,
                path: '/sites'
            },
            {
                id: 'updates',
                label: 'All Updates',
                icon: RefreshCw,
                path: '/sites/updates'
            },
            {
                id: 'backups',
                label: 'All Backups',
                icon: Database,
                path: '/sites/backups'
            },
            {
                id: 'hosting',
                label: 'Hosting',
                icon: Database,
                path: '/sites/hosting'
            }
        ]
    },
    {
        id: 'calendar',
        label: 'Calendar',
        icon: Calendar,
        path: '/calendar'
    }
];

// ──────────────────────────────────────────────────────────────────────────────
// CRM Dashboard Navigation (Sales / Client Operations)
// ──────────────────────────────────────────────────────────────────────────────

export const crmNavigationItems = [
    {
        id: 'crm-home',
        label: 'Home',
        icon: Home,
        path: '/crm/home'
    },
    {
        id: 'crm',
        label: 'CRM',
        icon: Zap,
        path: '/crm',
        hasSubItems: true,
        subItems: [
            {
                id: 'leads',
                label: 'Leads',
                icon: UserPlus,
                path: '/crm/leads'
            },
            {
                id: 'deals',
                label: 'Deals',
                icon: Handshake,
                path: '/deals'
            },
            {
                id: 'clients',
                label: 'Clients',
                icon: UserCheck,
                path: '/crm/clients'
            },
            {
                id: 'proposals',
                label: 'Proposals',
                icon: FileText,
                path: '/proposals'
            },
            {
                id: 'invoices',
                label: 'Invoices',
                icon: Receipt,
                path: '/crm/invoices'
            }
        ]
    },
    {
        id: 'services',
        label: 'Services',
        icon: Server,
        path: '/services',
        hasSubItems: true,
        subItems: [
            {
                id: 'catalog',
                label: 'Catalog',
                icon: LayoutGrid,
                path: '/services/catalog'
            },
            {
                id: 'orders',
                label: 'Orders',
                icon: ShoppingCart,
                path: '/services/orders'
            },
            {
                id: 'subscriptions',
                label: 'Subscriptions',
                icon: Repeat,
                path: '/services/subscriptions'
            },
            {
                id: 'coupons',
                label: 'Coupons',
                icon: Tag,
                path: '/services/coupons'
            }
        ]
    },
    {
        id: 'forms',
        label: 'Forms',
        icon: LayoutGrid,
        path: '/forms'
    },
    {
        id: 'support-tickets',
        label: 'Tickets',
        icon: Ticket,
        path: '/tickets'
    }
];

// ──────────────────────────────────────────────────────────────────────────────
// Helper: Get navigation items by dashboard mode
// ──────────────────────────────────────────────────────────────────────────────

export function getNavigationItems(mode: DashboardMode) {
    return mode === 'crm' ? crmNavigationItems : agencyNavigationItems;
}

// ──────────────────────────────────────────────────────────────────────────────
// Legacy export — combined list for backward compatibility
// ──────────────────────────────────────────────────────────────────────────────

export const navigationItems = [...agencyNavigationItems, ...crmNavigationItems];

// ──────────────────────────────────────────────────────────────────────────────
// Default hidden menu IDs
// ──────────────────────────────────────────────────────────────────────────────

export const DEFAULT_HIDDEN_MENU_IDS: string[] = [];

// ──────────────────────────────────────────────────────────────────────────────
// Site Detail Navigation (sub-navigation within /sites/:id/)
// ──────────────────────────────────────────────────────────────────────────────

export const siteDetailNavigationItems = [
    {
        id: 'overview',
        label: 'Overview',
        icon: LayoutDashboard,
        path: 'overview'
    },
    {
        id: 'content',
        label: 'Content',
        icon: LayoutGrid,
        path: 'content'
    },
    {
        id: 'backups',
        label: 'Backups',
        icon: Database,
        path: 'backups'
    },
    {
        id: 'uptime',
        label: 'Uptime Monitor',
        icon: Activity,
        path: 'uptime'
    },
    {
        id: 'health',
        label: 'Health & Security',
        icon: Shield,
        path: 'health'
    },
    {
        id: 'entries',
        label: 'Entries',
        icon: MessageSquare,
        path: 'entries'
    },
    {
        id: 'wp-users',
        label: 'WP Users',
        icon: Users,
        path: 'wp-users'
    },
    {
        id: 'settings',
        label: 'Site Settings',
        icon: Settings,
        path: 'settings'
    }
];
